/**
 * MusicDirector: the pure, headless scheduler behind the layered generative
 * music system (LOCKED-DESIGN 18.4 / 2.2). The old single "intensity" scalar
 * is unbundled into three independent axes:
 *
 *   PHASE (1-6)  -> arrangement: which layers play and how dense their
 *                   patterns are, so each 30-second phase is audibly distinct.
 *   CHAIN (0-4)  -> percussion bus: x1 is silent, higher tiers add hat, kick,
 *                   and snare density.
 *   PRIORITY     -> alarm/fuse ducking lives in mix.ts (PriorityDucker); the
 *                   director never competes with it.
 *
 * Tempo grid: 120 BPM in 4/4 on a 16th-note step grid. At the fixed 60 Hz
 * simulation tick this is exactly 30 ticks per beat and 120 ticks per bar, so
 * one 30-second phase is exactly 15 bars — escalation lands on the grid the
 * game counts in. Axis changes are QUANTIZED to bar boundaries so escalation
 * feels composed, not switched.
 *
 * No WebAudio in this file: the director emits MusicNote descriptions and the
 * Sound class renders them. That keeps the whole scheduling contract
 * unit-testable in a headless runner.
 */

export const BPM = 120;
export const SECONDS_PER_BEAT = 60 / BPM; // 0.5 s = 30 simulation ticks
export const STEPS_PER_BEAT = 4; // 16th-note grid
export const STEP_SECONDS = SECONDS_PER_BEAT / STEPS_PER_BEAT;
export const STEPS_PER_BAR = 16; // 4/4
export const BAR_SECONDS = STEPS_PER_BAR * STEP_SECONDS; // 2 s = 120 ticks

export type MusicVoice = "pad" | "bass" | "arp" | "lead" | "hat" | "kick" | "snare";

export interface MusicNote {
  voice: MusicVoice;
  /** Absolute time on the AudioContext clock. */
  time: number;
  /** Hz. Percussion voices use it as a tone hint. */
  freq: number;
  /** Pre-bus level, 0..1. */
  gain: number;
  /** Seconds. */
  dur: number;
}

// The phase-1 bass identity is the seed of the old praised loop: A1 A1 C2 G1.
const A1 = 55;
const G1 = 49;
const C2 = 65.41;
const G2 = 98;
const A2 = 110;

type BassPattern = "sparse" | "drive" | "surge";

/** [stepInBar, freq] pairs. */
const BASS_PATTERNS: Record<BassPattern, ReadonlyArray<readonly [number, number]>> = {
  sparse: [[0, A1], [4, A1], [8, C2], [12, G1]],
  drive: [[0, A1], [4, A1], [6, A2], [8, C2], [12, G1], [14, G2]],
  surge: [[0, A1], [2, A1], [4, A1], [6, A2], [8, C2], [10, C2], [12, G1], [14, G2]],
};

// A-minor pentatonic ladders for the arp voice.
const ARP_8 = [220, 261.63, 329.63, 392, 440, 392, 329.63, 261.63] as const;
const ARP_16 = [
  220, 261.63, 293.66, 329.63, 392, 440, 523.25, 440,
  392, 329.63, 293.66, 261.63, 220, 261.63, 329.63, 392,
] as const;

/** Lead motif over TWO bars: [stepInTwoBars, freq]. Density 2 adds the tail. */
const LEAD_MOTIF: ReadonlyArray<readonly [number, number]> = [
  [0, 659.25], [6, 587.33], [10, 523.25], [16, 440], [22, 523.25],
];
const LEAD_TAIL: ReadonlyArray<readonly [number, number]> = [[26, 587.33], [30, 659.25]];

// Percussion step masks.
const HAT_OFFBEAT = [2, 6, 10, 14] as const;
const HAT_DENSE = [0, 2, 4, 6, 8, 10, 12, 14] as const;
const KICK_BASIC = [0, 8] as const;
const KICK_FLOOR = [0, 4, 8, 12] as const;
const SNARE_BACK = [4, 12] as const;

const GAIN = { bass: 0.13, arp: 0.055, lead: 0.06, hatAccent: 0.05, hatGhost: 0.032, kick: 0.17, snare: 0.1 } as const;
const DUR = { bass: 0.42, arp: 0.16, lead: 0.5, hat: 0.05, kick: 0.14, snare: 0.1 } as const;

export interface Arrangement {
  layers: readonly MusicVoice[];
  bassPattern: BassPattern;
  /** Arp notes per bar (0 = layer off). */
  arpRate: 0 | 8 | 16;
  /** 0 = off, 1 = sparse motif, 2 = motif + tail. */
  leadDensity: 0 | 1 | 2;
  /** Phase 5+ widens the pad with a fifth — harmonic density, not volume. */
  padFifth: boolean;
  padLevel: number;
  /** Base lowpass cutoff before overflow tension is added. */
  cutoffHz: number;
}

const ARRANGEMENTS: readonly Arrangement[] = [
  { layers: ["pad", "bass"], bassPattern: "sparse", arpRate: 0, leadDensity: 0, padFifth: false, padLevel: 0.045, cutoffHz: 620 },
  { layers: ["pad", "bass", "arp"], bassPattern: "sparse", arpRate: 8, leadDensity: 0, padFifth: false, padLevel: 0.05, cutoffHz: 850 },
  { layers: ["pad", "bass", "arp"], bassPattern: "drive", arpRate: 8, leadDensity: 0, padFifth: false, padLevel: 0.055, cutoffHz: 1100 },
  { layers: ["pad", "bass", "arp", "lead"], bassPattern: "drive", arpRate: 16, leadDensity: 1, padFifth: false, padLevel: 0.06, cutoffHz: 1400 },
  { layers: ["pad", "bass", "arp", "lead"], bassPattern: "surge", arpRate: 16, leadDensity: 1, padFifth: true, padLevel: 0.065, cutoffHz: 1750 },
  { layers: ["pad", "bass", "arp", "lead"], bassPattern: "surge", arpRate: 16, leadDensity: 2, padFifth: true, padLevel: 0.07, cutoffHz: 2100 },
];

export function clampPhase(phase: number): number {
  return Math.max(1, Math.min(6, Math.floor(phase)));
}

export function clampChain(tier: number): number {
  return Math.max(0, Math.min(4, Math.floor(tier)));
}

export function arrangementForPhase(phase: number): Arrangement {
  return ARRANGEMENTS[clampPhase(phase) - 1]!;
}

/** Which arrangement layers are audible in a given phase. */
export function layersForPhase(phase: number): readonly MusicVoice[] {
  return arrangementForPhase(phase).layers;
}

/** Which percussion voices the chain tier enables (x1 / tier 0 = silence). */
export function percussionForChain(tier: number): readonly MusicVoice[] {
  const t = clampChain(tier);
  if (t <= 0) return [];
  if (t === 1) return ["hat"];
  if (t === 2) return ["hat", "kick"];
  return ["hat", "kick", "snare"]; // tier 4 keeps the set but densifies patterns
}

/** Lowpass cutoff: brightens per phase, with overflow pressure adding tension. */
export function musicFilterCutoff(phase: number, overflow01: number): number {
  return arrangementForPhase(phase).cutoffHz + Math.max(0, Math.min(1, overflow01)) * 1400;
}

export class MusicDirector {
  private step = 0;
  private nextStepTime: number;
  private targetPhase = 1;
  private targetChain = 0;
  private currentPhase = 1;
  private currentChain = 0;

  constructor(startTime: number) {
    this.nextStepTime = startTime;
  }

  /** Set the axis targets; they take effect at the NEXT bar boundary. */
  setAxes(phase: number, chainTier: number): void {
    this.targetPhase = clampPhase(phase);
    this.targetChain = clampChain(chainTier);
  }

  /** The arrangement phase actually playing (bar-quantized). */
  get appliedPhase(): number {
    return this.currentPhase;
  }

  /** The percussion tier actually playing (bar-quantized). */
  get appliedChain(): number {
    return this.currentChain;
  }

  /**
   * Emit every note whose start time lies in [cursor, until). Idempotent per
   * time window: the internal step cursor only moves forward, so calling
   * twice with the same horizon emits nothing new. `now` is the current
   * context time; `until` is the lookahead horizon.
   */
  schedule(now: number, until: number, sink: (note: MusicNote) => void): void {
    // Catch-up guard: after a long stall (hidden tab pauses RAF while the
    // context clock keeps running) jump the cursor to the present in whole
    // steps instead of flooding the sink with stale notes that would all
    // sound at once. Whole-step jumps keep the grid intact.
    const stepsBehind = Math.floor((now - this.nextStepTime) / STEP_SECONDS);
    if (stepsBehind > STEPS_PER_BAR) {
      this.step += stepsBehind;
      this.nextStepTime += stepsBehind * STEP_SECONDS;
    }
    while (this.nextStepTime < until) {
      if (this.step % STEPS_PER_BAR === 0) {
        this.currentPhase = this.targetPhase;
        this.currentChain = this.targetChain;
      }
      this.emitStep(this.step % STEPS_PER_BAR, Math.floor(this.step / STEPS_PER_BAR), this.nextStepTime, sink);
      this.step++;
      this.nextStepTime += STEP_SECONDS;
    }
  }

  private emitStep(stepInBar: number, barIndex: number, t: number, sink: (note: MusicNote) => void): void {
    const arr = arrangementForPhase(this.currentPhase);

    for (const [s, freq] of BASS_PATTERNS[arr.bassPattern]) {
      if (s === stepInBar) sink({ voice: "bass", time: t, freq, gain: GAIN.bass, dur: DUR.bass });
    }

    if (arr.arpRate === 8) {
      if (stepInBar % 2 === 0) {
        sink({ voice: "arp", time: t, freq: ARP_8[stepInBar / 2]!, gain: GAIN.arp, dur: DUR.arp });
      }
    } else if (arr.arpRate === 16) {
      sink({ voice: "arp", time: t, freq: ARP_16[stepInBar]!, gain: GAIN.arp, dur: DUR.arp });
    }

    if (arr.leadDensity > 0) {
      const leadStep = (barIndex % 2) * STEPS_PER_BAR + stepInBar;
      for (const [s, freq] of LEAD_MOTIF) {
        if (s === leadStep) sink({ voice: "lead", time: t, freq, gain: GAIN.lead, dur: DUR.lead });
      }
      if (arr.leadDensity >= 2) {
        for (const [s, freq] of LEAD_TAIL) {
          if (s === leadStep) sink({ voice: "lead", time: t, freq, gain: GAIN.lead, dur: DUR.lead });
        }
      }
    }

    const perc = percussionForChain(this.currentChain);
    if (perc.includes("hat")) {
      const steps: readonly number[] = this.currentChain >= 4 ? HAT_DENSE : HAT_OFFBEAT;
      if (steps.includes(stepInBar)) {
        const accent = (HAT_OFFBEAT as readonly number[]).includes(stepInBar);
        sink({ voice: "hat", time: t, freq: 7200, gain: accent ? GAIN.hatAccent : GAIN.hatGhost, dur: DUR.hat });
      }
    }
    if (perc.includes("kick")) {
      const steps: readonly number[] = this.currentChain >= 4 ? KICK_FLOOR : KICK_BASIC;
      if (steps.includes(stepInBar)) sink({ voice: "kick", time: t, freq: 150, gain: GAIN.kick, dur: DUR.kick });
    }
    if (perc.includes("snare") && (SNARE_BACK as readonly number[]).includes(stepInBar)) {
      sink({ voice: "snare", time: t, freq: 1800, gain: GAIN.snare, dur: DUR.snare });
    }
  }
}
